import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2, CalendarDays, CheckCircle2, Clock3, ExternalLink,
  Loader2, Pencil, RefreshCw, Server
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CsvExportButton from '@/components/CsvExportButton';
import ClickUpTaskButton from '@/components/ClickUpTaskButton';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const formatHours = value => `${Number(value || 0).toFixed(1)}h`;
const statusStyle = {
  not_generated: 'bg-gray-100 text-gray-600',
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  skipped: 'bg-slate-100 text-slate-600',
};

const MaintenanceSchedule = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = user?.role === 'admin' || user?.role === 'subadmin';
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'servers' ? 'servers' : 'websites');
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [servers, setServers] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assignedUserId, setAssignedUserId] = useState(user?.id || '');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [companyCheckResult, setCompanyCheckResult] = useState(null);
  const [editSchedule, setEditSchedule] = useState(null);
  const [editForm, setEditForm] = useState({ day_of_month: '1', estimate_hours: '0.333' });

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scheduleRes, serverRes, assigneeRes] = await Promise.all([
        axios.get(`${API}/maintenance-schedules?month_year=${month}`, auth()),
        axios.get(`${API}/maintenance-servers?month_year=${month}`, auth()),
        axios.get(`${API}/maintenance-schedules/assignees`, auth()),
      ]);
      setData(scheduleRes.data);
      setServers(serverRes.data || []);
      setAssignees(assigneeRes.data || []);
      if (!assignedUserId && assigneeRes.data?.length) {
        const preferred = assigneeRes.data.find(item => item.id === user?.id) || assigneeRes.data[0];
        setAssignedUserId(preferred.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load maintenance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [month]); // eslint-disable-line

  useEffect(() => {
    if (!canManage) return;
    axios.post(`${API}/maintenance-servers/generate-weekly?month_year=${month}`, {}, auth())
      .then(result => {
        if (result.data.created > 0) fetchData();
      })
      .catch(() => toast.error('Failed to generate weekly server maintenance'));
  }, [month]); // eslint-disable-line

  const websiteSchedules = useMemo(
    () => (data?.schedules || []).filter(item => item.schedule_type === 'company'),
    [data]
  );

  const checkAndAddCompanies = async () => {
    if (!assignedUserId) return toast.error('Select the support person');
    setWorking(true);
    try {
      const allocation = await axios.post(
        `${API}/maintenance-schedules/auto-assign?month_year=${month}`,
        { assigned_user_id: assignedUserId, max_companies: 1000, max_per_day: 8 },
        auth()
      );
      await axios.post(`${API}/maintenance-schedules/generate?month_year=${month}`, {}, auth());
      setCompanyCheckResult(allocation.data);
      toast.success(
        allocation.data.created > 0
          ? `${allocation.data.created} new ${allocation.data.created === 1 ? 'company' : 'companies'} added to maintenance`
          : 'All eligible companies are already scheduled'
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Automatic assignment failed');
    } finally {
      setWorking(false);
    }
  };

  const saveScheduleEdit = async event => {
    event.preventDefault();
    setWorking(true);
    try {
      await axios.patch(
        `${API}/maintenance-schedules/${editSchedule.id}?month_year=${month}`,
        {
          day_of_month: Number(editForm.day_of_month),
          estimate_hours: Number(editForm.estimate_hours)
        },
        auth()
      );
      toast.success('Schedule updated in the app, report history, and ClickUp');
      setEditSchedule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setWorking(false);
    }
  };

  const completeTask = async instanceId => {
    try {
      await axios.patch(`${API}/maintenance-schedules/instances/${instanceId}`, { status: 'completed' }, auth());
      toast.success('Maintenance completed and added to history');
      fetchData();
    } catch {
      toast.error('Failed to complete maintenance');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Maintenance</h1>
            <p className="text-sm text-gray-500">Monthly website workload and server maintenance history</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="month" value={month} onChange={event => setMonth(event.target.value)} className="w-40" />
            <Button variant="outline" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={value => {
          setActiveTab(value);
          setSearchParams(value === 'servers' ? { tab: 'servers' } : {});
        }}>
          <TabsList className="mb-6">
            <TabsTrigger value="websites" className="gap-2"><Building2 className="w-4 h-4" />Website Maintenance</TabsTrigger>
            <TabsTrigger value="servers" className="gap-2"><Server className="w-4 h-4" />Server Maintenance</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : (
            <>
              <TabsContent value="websites">
                <Card className="mb-6 border-indigo-200 bg-indigo-50/40">
                  <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold text-gray-900">Check for new companies</p>
                      <p className="text-sm text-gray-600">Adds only eligible companies that do not already have a recurring schedule. Existing dates and tasks stay unchanged.</p>
                      {companyCheckResult && (
                        <div className="flex flex-wrap gap-2 mt-2 text-xs">
                          <Badge variant="outline" className="bg-white">{companyCheckResult.created} added</Badge>
                          <Badge variant="outline" className="bg-white">{companyCheckResult.already_scheduled} already scheduled</Badge>
                          <Badge variant="outline" className="bg-white">{companyCheckResult.excluded} excluded</Badge>
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Select value={assignedUserId} onValueChange={setAssignedUserId}>
                          <SelectTrigger className="w-full sm:w-64 bg-white"><SelectValue placeholder="Support person" /></SelectTrigger>
                          <SelectContent>
                            {assignees.map(person => <SelectItem key={person.id} value={person.id}>{person.name || person.email}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button onClick={checkAndAddCompanies} disabled={working}>
                          {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-2" />}
                          Check & Add New Companies
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {[
                    ['Websites', websiteSchedules.length, Building2],
                    ['Planned Hours', formatHours(websiteSchedules.reduce((sum, item) => sum + item.estimate_hours, 0)), Clock3],
                    ['Completed', websiteSchedules.filter(item => item.instance?.status === 'completed').length, CheckCircle2],
                    ['ClickUp Synced', websiteSchedules.filter(item => item.instance?.clickup_task_id).length, ExternalLink],
                  ].map(([label, value, Icon]) => (
                    <Card key={label}><CardContent className="p-4"><Icon className="w-5 h-5 text-indigo-600 mb-2" /><p className="text-xs text-gray-500 uppercase font-semibold">{label}</p><p className="text-2xl font-bold">{value}</p></CardContent></Card>
                  ))}
                </div>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base">Website Schedule</CardTitle>
                    <CsvExportButton
                      filename={`website-maintenance-${month}`}
                      rows={websiteSchedules}
                      columns={[
                        { label: 'Company', value: 'company_name' },
                        { label: 'Maintenance Date', value: 'due_date' },
                        { label: 'Status', value: row => row.instance?.status || 'not generated' },
                        { label: 'Estimated Hours', value: 'estimate_hours' },
                        { label: 'Assigned To', value: 'assigned_user_email' },
                        { label: 'ClickUp Task ID', value: row => row.instance?.clickup_task_id || '' },
                      ]}
                    />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {websiteSchedules.map(item => (
                        <div key={item.id} className="border rounded-lg p-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div>
                            <button onClick={() => navigate(`/admin/companies/${item.company_id}`)} className="font-semibold text-left hover:text-indigo-700">{item.company_name}</button>
                            <p className="text-xs text-gray-500">{item.due_date} · 20 minutes · {item.assigned_user_email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={statusStyle[item.instance?.status || 'not_generated']}>{(item.instance?.status || 'not generated').replace('_', ' ')}</Badge>
                            <ClickUpTaskButton taskId={item.instance?.clickup_task_id} iconOnly className="h-8 w-8 p-0" />
                            {item.instance && item.instance.status !== 'completed' && <Button size="sm" variant="outline" onClick={() => completeTask(item.instance.id)}>Complete</Button>}
                            {canManage && <Button size="sm" variant="ghost" onClick={() => { setEditSchedule(item); setEditForm({ day_of_month: String(item.day_of_month), estimate_hours: String(item.estimate_hours) }); }}><Pencil className="w-4 h-4" /></Button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="servers">
                <div className="flex justify-end gap-2 mb-4">
                  <CsvExportButton
                    filename={`server-maintenance-${month}`}
                    rows={servers}
                    columns={[
                      { label: 'Server', value: 'name' },
                      { label: 'Hosted Sites', value: 'site_count' },
                      { label: 'Last Maintenance', value: row => row.last_maintenance?.maintenance_date || '' },
                      { label: 'Last Status', value: row => row.last_maintenance?.status || '' },
                      { label: 'Weekly Tasks', value: row => row.weekly_tasks?.length || 0 },
                      { label: 'Assigned To', value: row => row.schedule?.assigned_user_email || '' },
                    ]}
                  />
                </div>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {servers.map(server => (
                    <Card key={server.name} className="hover:shadow-md transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="w-10 h-10 bg-amber-100 text-amber-700 rounded-lg flex items-center justify-center"><Server className="w-5 h-5" /></div>
                          <Badge variant="outline">{server.site_count} sites</Badge>
                        </div>
                        <CardTitle className="text-lg mt-3">{server.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm text-gray-600 space-y-2 mb-4">
                          <p><span className="font-medium">Last maintenance:</span> {server.last_maintenance?.maintenance_date || 'Not recorded'}</p>
                          <p><span className="font-medium">Weekly schedule:</span> Mondays · 20 minutes</p>
                          <div className="flex flex-wrap gap-1 pt-1">
                            {(server.weekly_tasks || []).map(task => (
                              <span key={task.id} className="inline-flex items-center gap-1">
                                <Badge className={statusStyle[task.status] || statusStyle.scheduled}>
                                  {task.maintenance_date.slice(5)} · {task.status.replace('_', ' ')}
                                </Badge>
                                <ClickUpTaskButton taskId={task.clickup_task_id} iconOnly className="h-6 w-6 p-0" />
                              </span>
                            ))}
                          </div>
                        </div>
                        <Button className="w-full" variant="outline" onClick={() => navigate(`/maintenance/servers/${encodeURIComponent(server.name)}?from=servers`)}>Open Server</Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>

      <Dialog open={Boolean(editSchedule)} onOpenChange={open => !open && setEditSchedule(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust Website Schedule</DialogTitle></DialogHeader>
          <form onSubmit={saveScheduleEdit} className="space-y-4">
            <div><Label>Day of Month</Label><Input type="number" min="1" max="31" value={editForm.day_of_month} onChange={e => setEditForm({ ...editForm, day_of_month: e.target.value })} /></div>
            <div><Label>Estimated Hours</Label><Input type="number" min="0.1" step="0.01" value={editForm.estimate_hours} onChange={e => setEditForm({ ...editForm, estimate_hours: e.target.value })} /></div>
            <Button className="w-full" disabled={working}>Save Schedule</Button>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default MaintenanceSchedule;
